import * as React from 'react';
import { createPortal } from 'react-dom';
import { Loader2, MapPin } from 'lucide-react';

import { cn } from '@/shared/lib/utils';
import { Input } from '@/shared/ui/input';

import { hereMapsService } from '@/services/hereMapsService';
import { parseHereAddress } from '@/utils/addressParser';
import type { HereAddressItem, StructuredAddress } from '@/types/address';

export interface AddressAutocompleteProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  /** Current free-text value of the input. */
  value: string;
  /** Called with the typed value on every keystroke. */
  onChange: (value: string) => void;
  /** Called when the user picks a suggestion. Provides the parsed structured
   *  address (street/suburb/city/region/postcode + lat/lng). */
  onAddressSelect?: (address: StructuredAddress) => void;
  /** Placeholder shown when empty. */
  placeholder?: string;
  /** Whether the input is invalid (e.g. failed RHF validation). */
  invalid?: boolean;
  /** Disable input + autocomplete. */
  disabled?: boolean;
  /** Min characters before search fires. Default 3. */
  minLength?: number;
  /** Debounce delay (ms). Default 400. */
  debounceMs?: number;
}

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
}

/**
 * AddressAutocomplete — shadcn-styled address input wired to HERE Maps Discover API.
 *
 * The suggestions dropdown is rendered into a `document.body` portal so it can
 * escape parent `overflow: hidden` containers (e.g. cards) and float above
 * everything else on the page.
 *
 * Keyboard support:
 *   - ↓/↑ arrows: navigate suggestions
 *   - Enter: pick highlighted suggestion
 *   - Esc: close dropdown
 */
export const AddressAutocomplete = React.forwardRef<
  HTMLInputElement,
  AddressAutocompleteProps
>(function AddressAutocomplete(
  {
    value,
    onChange,
    onAddressSelect,
    onBlur,
    placeholder = 'Start typing an address…',
    invalid = false,
    disabled = false,
    minLength = 3,
    debounceMs = 400,
    className,
    ...rest
  },
  forwardedRef,
) {
  const [suggestions, setSuggestions] = React.useState<HereAddressItem[]>([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [highlight, setHighlight] = React.useState(0);
  const [position, setPosition] = React.useState<DropdownPosition>({
    top: 0,
    left: 0,
    width: 0,
  });

  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = React.useRef<AbortController | null>(null);
  const cacheRef = React.useRef<Map<string, HereAddressItem[]>>(new Map());
  const localInputRef = React.useRef<HTMLInputElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const dropdownRef = React.useRef<HTMLUListElement>(null);

  // Merge forwarded ref with local ref so the parent (e.g. RHF) can still focus.
  React.useImperativeHandle(forwardedRef, () => localInputRef.current as HTMLInputElement);

  // Position the portal dropdown directly underneath the input.
  const updatePosition = React.useCallback(() => {
    const input = localInputRef.current;
    if (!input) return;
    const rect = input.getBoundingClientRect();
    setPosition({
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
  }, []);

  // Recalc position whenever the dropdown opens or on scroll/resize while open.
  React.useEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  // Cancel pending work when unmounting.
  React.useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  // Close dropdown when clicking outside the input + portal dropdown.
  React.useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      const inInput = containerRef.current?.contains(target);
      const inDropdown = dropdownRef.current?.contains(target);
      if (!inInput && !inDropdown) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  function runSearch(query: string) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();

    const trimmed = query.trim();
    if (trimmed.length < minLength) {
      setSuggestions([]);
      setOpen(false);
      setLoading(false);
      return;
    }

    const cacheKey = trimmed.toLowerCase();
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setSuggestions(cached);
      setOpen(cached.length > 0);
      setHighlight(0);
      setLoading(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);

      try {
        const response = await hereMapsService.searchAddresses(
          trimmed,
          controller.signal,
        );
        const items = response.items ?? [];
        cacheRef.current.set(cacheKey, items);
        setSuggestions(items);
        setOpen(items.length > 0);
        setHighlight(0);
      } catch (err) {
        // AbortError is expected when a new search starts — ignore it.
        if ((err as Error).name !== 'AbortError') {
          console.error('Address search failed:', err);
          setSuggestions([]);
          setOpen(false);
        }
      } finally {
        setLoading(false);
      }
    }, debounceMs);
  }

  async function handleSelect(item: HereAddressItem) {
    setOpen(false);
    setSuggestions([]);

    // Show the picked label immediately for responsiveness.
    const immediate = parseHereAddress(item);
    onChange(immediate.fullAddress);

    // Autosuggest items only carry a combined address label; look the id up to
    // recover the structured street/suburb/city/postcode for the form fields.
    let parsed = immediate;
    if (item.id) {
      try {
        const detailed = await hereMapsService.lookupAddress(item.id);
        if (detailed) {
          parsed = parseHereAddress(detailed);
          onChange(parsed.fullAddress);
        }
      } catch {
        // Lookup failed (or was aborted) — keep the immediate parse.
      }
    }
    onAddressSelect?.(parsed);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSelect(suggestions[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div ref={containerRef} className="tw-relative">
      <MapPin className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-h-4 tw-w-4 tw-text-muted-foreground" />
      <Input
        ref={localInputRef}
        type="text"
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        aria-invalid={invalid || undefined}
        aria-autocomplete="list"
        aria-expanded={open}
        onChange={(e) => {
          onChange(e.target.value);
          runSearch(e.target.value);
        }}
        onFocus={() => {
          if (suggestions.length > 0) {
            updatePosition();
            setOpen(true);
          }
        }}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        className={cn(
          'tw-pl-9',
          loading && 'tw-pr-9',
          invalid && 'tw-border-destructive focus-visible:tw-ring-destructive',
          className,
        )}
        {...rest}
      />
      {loading ? (
        <Loader2 className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-h-4 tw-w-4 tw-animate-spin tw-text-muted-foreground" />
      ) : null}

      {open && suggestions.length > 0 && typeof document !== 'undefined'
        ? createPortal(
            <ul
              ref={dropdownRef}
              role="listbox"
              style={{
                position: 'absolute',
                top: position.top,
                left: position.left,
                width: position.width,
                zIndex: 9999,
              }}
              className="tw-max-h-72 tw-overflow-auto tw-rounded-md tw-border tw-border-border tw-bg-popover tw-p-1 tw-text-popover-foreground tw-shadow-lg tw-animate-in tw-fade-in-0 tw-zoom-in-95"
            >
              {suggestions.map((item, idx) => {
                const isActive = idx === highlight;
                return (
                  <li
                    key={item.id ?? `${item.title}-${idx}`}
                    role="option"
                    aria-selected={isActive}
                    onMouseEnter={() => setHighlight(idx)}
                    onMouseDown={(e) => {
                      // Prevent the input's blur from firing before we capture the click.
                      e.preventDefault();
                      handleSelect(item);
                    }}
                    className={cn(
                      'tw-flex tw-cursor-pointer tw-items-start tw-gap-2 tw-rounded-md tw-px-2.5 tw-py-2 tw-text-sm tw-transition-colors',
                      isActive
                        ? 'tw-bg-primary/10 tw-text-foreground'
                        : 'tw-text-foreground hover:tw-bg-muted',
                    )}
                  >
                    <MapPin
                      className={cn(
                        'tw-mt-0.5 tw-h-3.5 tw-w-3.5 tw-shrink-0',
                        isActive ? 'tw-text-primary' : 'tw-text-muted-foreground',
                      )}
                    />
                    <span className="tw-truncate">{item.title}</span>
                  </li>
                );
              })}
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
});
